/**
 * SellerOnboardingPlan — the post-KYB paywall. New sellers land here
 * automatically until they pick a plan. Once subscribed, the
 * SellerBillingGate stops redirecting and they reach the dashboard.
 *
 * Design intent: minimal-friction "pick + activate" — 3 plan cards,
 * one "Subscribe" button per card, wallet-low warning if balance can't
 * cover the first month's charge with a one-click jump to the topup
 * flow. Matches the visual language of `PlanBillingPanel` so sellers
 * see consistent pricing language across signup + ongoing management.
 */
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { useSellerBilling } from "../../context/SellerBillingContext";
import { Check, Loader2, Sparkles, ArrowRight, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/button";


function cents(c) {
  return `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}


const PLAN_PERKS = {
  starter:    ["All payments + invoicing", "Trade credit insurance access", "Get paid today (factoring)", "Credit memos + pre-credit pools", "Bulk pay (buyer-side)", "Owner-only — no team seats"],
  growth:     ["Everything in Starter", "Up to 5 team seats (admin/operator/viewer roles)", "Priority chat + email support", "Branded buyer storefront", "Custom invoice T&Cs"],
  pro:        ["Everything in Growth", "Up to 20 team seats", "Advanced reporting + custom dashboards", "Multi-warehouse + multi-region", "Dedicated onboarding specialist", "API access (standard rate limits)"],
  enterprise: ["Everything in Pro", "Unlimited team seats", "SSO + advanced permissions", "Custom contract pricing", "Dedicated CSM", "Scaled API access"],
};


export default function SellerOnboardingPlan() {
  const nav = useNavigate();
  const { refresh } = useSellerBilling();
  const [plans, setPlans]       = useState([]);
  const [wallet, setWallet]     = useState(null);
  const [busy, setBusy]         = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [planRes, walletRes] = await Promise.all([
          api.get("/billing/plans"),
          api.get("/wallet"),
        ]);
        setPlans(planRes.data.plans);
        setWallet(walletRes.data);
      } catch (e) {
        toast.error("Couldn't load plans. Refresh to retry.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onSubscribe = async (planId) => {
    setBusy(planId);
    try {
      await api.post("/billing/subscribe", { plan_id: planId });
      toast.success("Plan activated. Welcome to verqo!");
      await refresh();
      nav("/seller", { replace: true });
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      if (code === "insufficient_wallet_for_subscription") {
        toast.error("Your wallet is short for this plan. Top up and try again.");
      } else {
        toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Subscribe failed.");
      }
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#fdfbf7]">
        <Loader2 className="w-6 h-6 animate-spin text-foreground/40"/>
      </div>
    );
  }

  const balanceCents = Math.round(((wallet?.balance ?? 0) * 100));

  return (
    <div className="min-h-screen bg-[#fdfbf7]" data-testid="seller-onboarding-plan-page">
      <div className="page-shell-narrow py-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center">
            <Sparkles className="w-5 h-5"/>
          </div>
          <div className="text-xs uppercase tracking-widest font-medium text-foreground/60">
            Step 2 of 2 · activate verqo
          </div>
        </div>
        <h1 className="text-4xl sm:text-5xl font-medium tracking-tight">Pick a plan to go live</h1>
        <p className="text-foreground/60 mt-3 max-w-2xl text-[15px]">
          Your subscription is billed monthly from your verqo wallet. No credit card surcharges, no
          GMV cut. Switch or cancel anytime. <strong>Buyers are always free.</strong>
        </p>

        {/* Wallet line */}
        <div className="mt-6 inline-flex items-center gap-2 text-[13px] text-foreground/70 bg-white border rounded-full px-4 py-2">
          <span>Wallet balance:</span>
          <span className="font-medium text-foreground">${(wallet?.balance ?? 0).toLocaleString(undefined, {minimumFractionDigits:2,maximumFractionDigits:2})}</span>
          <Link to="/seller/wallet" className="text-emerald-700 hover:underline ml-2">Top up</Link>
        </div>

        {/* Plan cards */}
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {plans.map(p => {
            const tooLow = balanceCents < p.base_fee_cents;
            return (
              <div key={p.id}
                   data-testid={`onboarding-plan-card-${p.id}`}
                   className={`rounded-2xl border bg-white p-7 flex flex-col ${p.id === "growth" ? "border-emerald-400 ring-2 ring-emerald-100" : "border-foreground/10"}`}>
                {p.id === "growth" && (
                  <div className="text-[10px] uppercase tracking-widest font-medium text-emerald-700 mb-2">
                    most popular
                  </div>
                )}
                <div className="text-xl font-medium">{p.name}</div>
                <div className="text-foreground/60 text-sm mt-1">{p.tagline || ""}</div>

                <div className="mt-5">
                  <span className="text-4xl font-medium tracking-tight">{cents(p.base_fee_cents)}</span>
                  <span className="text-foreground/60 text-sm">/ month</span>
                </div>
                <div className="text-foreground/60 text-[13px] mt-1">
                  {p.buyers_included} buyer{p.buyers_included === 1 ? "" : "s"} included · {cents(p.per_buyer_rate_cents)} each additional
                </div>

                <ul className="mt-6 space-y-2 flex-1">
                  {(PLAN_PERKS[p.id] || []).map((perk, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13.5px] text-foreground/80">
                      <Check className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0"/>
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>

                {tooLow && (
                  <div className="mt-5 flex items-start gap-2 text-[12.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
                    <div>
                      Wallet is short. Need {cents(p.base_fee_cents)} for this plan.
                      <Link to="/seller/wallet" className="ml-1 underline">Top up →</Link>
                    </div>
                  </div>
                )}

                <Button
                  data-testid={`onboarding-subscribe-${p.id}-btn`}
                  disabled={!!busy || tooLow}
                  onClick={() => onSubscribe(p.id)}
                  className={`mt-5 rounded-full ${p.id === "growth" ? "bg-emerald-700 hover:bg-emerald-700/90 text-white" : ""}`}>
                  {busy === p.id ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Activating…</>
                  ) : (
                    <>Subscribe · {cents(p.base_fee_cents)}/mo <ArrowRight className="w-4 h-4 ml-1"/></>
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-foreground/50 text-[12.5px] mt-8 max-w-3xl">
          The first month is deducted from your verqo wallet as soon as you subscribe. Subsequent
          months auto-deduct on your billing day. If your wallet runs short, you get a 3-day grace
          window before the account flips to read-only. Set up <strong>auto-topup from a card on
          file</strong> in Settings → Plan &amp; Billing once you&apos;re in.
        </p>
      </div>
    </div>
  );
}
